
import { useState } from 'react';
import { Comment, Reply } from '../../../mocks/comments';

interface CommentSectionProps {
  comments: Comment[];
  moduleIndex: number;
  onAddComment: (content: string, isQuestion: boolean) => void;
  onAddReply: (commentId: string, content: string) => void;
  onLikeComment: (commentId: string) => void;
  onLikeReply: (commentId: string, replyId: string) => void;
}

export default function CommentSection({
  comments,
  moduleIndex,
  onAddComment,
  onAddReply,
  onLikeComment,
  onLikeReply,
}: CommentSectionProps) {
  const [newComment, setNewComment] = useState('');
  const [isQuestion, setIsQuestion] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [filter, setFilter] = useState<'all' | 'questions' | 'comments'>('all');

  const moduleComments = comments.filter(c => c.moduleIndex === moduleIndex);
  
  const filteredComments = moduleComments.filter(c => {
    if (filter === 'questions') return c.isQuestion;
    if (filter === 'comments') return !c.isQuestion;
    return true;
  });

  const handleSubmitComment = () => {
    if (newComment.trim()) {
      onAddComment(newComment, isQuestion);
      setNewComment('');
      setIsQuestion(false);
    }
  };

  const handleSubmitReply = (commentId: string) => {
    if (replyContent.trim()) {
      onAddReply(commentId, replyContent);
      setReplyContent('');
      setReplyingTo(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="mt-8 bg-white rounded-2xl border-2 border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0099CC]/10 to-[#FFCC00]/10 p-6 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#0099CC] rounded-lg">
              <i className="ri-discuss-line text-xl text-white"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Comentários e Dúvidas</h3>
              <p className="text-sm text-gray-600">{moduleComments.length} participações neste módulo</p>
            </div>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex bg-white rounded-full p-1 shadow-sm border border-gray-200">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                filter === 'all'
                  ? 'bg-[#0099CC] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setFilter('questions')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                filter === 'questions'
                  ? 'bg-[#0099CC] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-question-line mr-1"></i>
              Dúvidas
            </button>
            <button
              onClick={() => setFilter('comments')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                filter === 'comments'
                  ? 'bg-[#0099CC] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-chat-3-line mr-1"></i>
              Comentários
            </button>
          </div>
        </div>
      </div>

      {/* New Comment Form */}
      <div className="p-6 border-b border-gray-100 bg-gray-50">
        <div className="flex gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B] rounded-full flex-shrink-0">
            <i className="ri-user-line text-xl text-white"></i>
          </div>
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value.slice(0, 500))}
              placeholder="Escreva seu comentário ou dúvida..."
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-[#0099CC] focus:outline-none resize-none text-sm"
              rows={3}
            />
            <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isQuestion}
                  onChange={(e) => setIsQuestion(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-[#0099CC] focus:ring-[#0099CC] cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  <i className="ri-question-line mr-1 text-[#CC1B1B]"></i>
                  Marcar como dúvida
                </span>
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">{newComment.length}/500</span>
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim()}
                  className="bg-[#0099CC] hover:bg-[#007AA3] disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Publicar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="divide-y divide-gray-100">
        {filteredComments.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-chat-3-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-600">Nenhum {filter === 'questions' ? 'dúvida' : filter === 'comments' ? 'comentário' : 'participação'} ainda.</p>
            <p className="text-sm text-gray-500 mt-1">Seja o primeiro a participar!</p>
          </div>
        ) : (
          filteredComments.map((comment) => (
            <div key={comment.id} className="p-6">
              {/* Comment */}
              <div className="flex gap-4">
                <img
                  src={comment.avatar}
                  alt={comment.author}
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{comment.author}</span>
                        {comment.isQuestion && (
                          <span className="bg-[#CC1B1B]/10 text-[#CC1B1B] px-2 py-0.5 rounded-full text-xs font-semibold">
                            <i className="ri-question-line mr-1"></i>
                            Dúvida
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(comment.date)}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mt-2 leading-relaxed">{comment.content}</p>
                  
                  <div className="flex items-center gap-4 mt-3">
                    <button
                      onClick={() => onLikeComment(comment.id)}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#CC1B1B] transition-colors cursor-pointer"
                    >
                      <i className="ri-heart-line"></i>
                      <span>{comment.likes}</span>
                    </button>
                    <button
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#0099CC] transition-colors cursor-pointer"
                    >
                      <i className="ri-reply-line"></i>
                      <span>Responder</span>
                    </button>
                    {comment.replies.length > 0 && (
                      <span className="text-sm text-gray-500">
                        <i className="ri-chat-1-line mr-1"></i>
                        {comment.replies.length} {comment.replies.length === 1 ? 'resposta' : 'respostas'}
                      </span>
                    )}
                  </div>

                  {/* Reply Form */}
                  {replyingTo === comment.id && (
                    <div className="mt-4 pl-4 border-l-2 border-[#0099CC]">
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value.slice(0, 500))}
                        placeholder="Escreva sua resposta..."
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-[#0099CC] focus:outline-none resize-none text-sm"
                        rows={2}
                      />
                      <div className="flex items-center justify-end gap-2 mt-2">
                        <button
                          onClick={() => setReplyingTo(null)}
                          className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => handleSubmitReply(comment.id)}
                          disabled={!replyContent.trim()}
                          className="bg-[#0099CC] hover:bg-[#007AA3] disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                        >
                          Responder
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {comment.replies.length > 0 && (
                    <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-200">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="flex gap-3">
                          <img
                            src={reply.avatar}
                            alt={reply.author}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-900">{reply.author}</span>
                              {reply.isInstructor && (
                                <span className="bg-[#0099CC]/10 text-[#0099CC] px-2 py-0.5 rounded-full text-xs font-semibold">
                                  <i className="ri-user-star-line mr-1"></i>
                                  Instrutor
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{formatDate(reply.date)}</span>
                            </div>
                            <p className="text-gray-700 mt-1 text-sm leading-relaxed">{reply.content}</p>
                            <button
                              onClick={() => onLikeReply(comment.id, reply.id)}
                              className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#CC1B1B] transition-colors mt-2 cursor-pointer"
                            >
                              <i className="ri-heart-line"></i>
                              <span>{reply.likes}</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
